/* Rangnoraa Living Wardrobe 3.2.6 — three-stop showroom and collection categories. */
const instances = new WeakMap();
const clamp = (n, min, max) => Math.min(max, Math.max(min, n));
const $ = (selector, root = document) => root.querySelector(selector);
const $$ = (selector, root = document) => [...root.querySelectorAll(selector)];

function parseJSON(script, fallback = []) {
  if (!script) return fallback;
  try { return JSON.parse(script.textContent) ?? fallback; } catch (_) { return fallback; }
}
function displayTitle(value) {
  return String(value || '').replace(/[_-]+/g, ' ').toLowerCase().replace(/\b[a-z]/g, letter => letter.toUpperCase());
}
function stripHTML(value) {
  const box = document.createElement('div');
  box.innerHTML = String(value || '');
  return (box.textContent || '').replace(/\s+/g, ' ').trim();
}

function initWardrobe(root) {
  if (instances.has(root)) return;

  const viewport = $('[data-wardrobe-viewport]', root);
  const canvasHost = $('[data-wardrobe-canvas]', root);
  const featuredScript = $('[data-wardrobe-products]', root);
  const orderedScript = $('[data-wardrobe-ordered-products]', root);
  const catalogScript = $('[data-wardrobe-catalog-products]', root);
  const categoryScript = $('[data-wardrobe-category-products]', root);
  if (!viewport || !canvasHost || !featuredScript) return;
  const focusPreview = $('[data-wardrobe-focus-preview]', viewport);
  const focusImage = $('[data-wardrobe-focus-image]', focusPreview);

  const featured = parseJSON(featuredScript).filter(Boolean);
  const ordered = parseJSON(orderedScript).filter(Boolean);
  const catalogSeed = parseJSON(catalogScript).filter(Boolean);
  const categorySeed = parseJSON(categoryScript).filter(entry => entry?.category && entry?.product?.handle);
  const itemByHandle = new Map();
  const catalogItems = [];
  const categoryProducts = new Map();

  const addItem = item => {
    if (!item?.handle) return;
    const existing = itemByHandle.get(item.handle);
    if (existing) {
      Object.keys(item).forEach(key => {
        if ((existing[key] === null || existing[key] === undefined || existing[key] === '') && item[key] !== null && item[key] !== undefined) existing[key] = item[key];
      });
      return existing;
    }
    item._hydrated = Array.isArray(item.variants) && item.variants.length > 0;
    itemByHandle.set(item.handle, item);
    catalogItems.push(item);
    return item;
  };
  /* Product order blocks define the carousel sequence; older settings keep the opening trio. */
  if (ordered.length) {
    ordered.forEach(addItem);
    featured.forEach(addItem);
  } else if (featured.length >= 3) [featured[1], featured[0], featured[2]].forEach(addItem);
  else featured.forEach(addItem);
  catalogSeed.forEach(addItem);
  categorySeed.forEach(({category, product}) => {
    if (!categoryProducts.has(category)) categoryProducts.set(category, new Set());
    categoryProducts.get(category).add(product.handle);
    addItem(product);
  });

  const shop = $('[data-wardrobe-shop]', viewport);
  const quickTitle = $('[data-wardrobe-quick-title]', shop);
  const quickPrice = $('[data-wardrobe-quick-price]', shop);
  const quickOptions = $('[data-wardrobe-quick-options]', shop);
  const quickVariant = $('[data-wardrobe-quick-variant]', shop);
  const quickQuantity = $('[data-wardrobe-quick-quantity]', shop);
  const quickBuy = $('[data-wardrobe-buy-now]', shop);
  const quickAdd = $('[data-wardrobe-quick-add]', shop);
  const quickStatus = $('[data-wardrobe-quick-status]', shop);
  const productDetailsLink = $('[data-wardrobe-product-details]', shop);
  const focusDetailsLink = $('[data-wardrobe-focus-details]', focusPreview);
  const setLabel = $('[data-wardrobe-set-label]', shop);

  const inspector = $('[data-wardrobe-inspector]', viewport);
  const catalogPanel = $('[data-wardrobe-catalog]', viewport);
  const catalogGrid = $('[data-wardrobe-catalog-grid]', catalogPanel);
  const categoryTrack = $('[data-wardrobe-category-track]', catalogPanel);
  const categoryButtons = $$('[data-wardrobe-category]', categoryTrack || catalogPanel);
  const categoryLink = $('[data-wardrobe-category-link]', catalogPanel);
  const categoryEmpty = $('[data-wardrobe-category-empty]', catalogPanel);
  const catalogCards = new Map();
  $$('[data-wardrobe-catalog-product]', catalogGrid).forEach(card => {
    const handle = card.dataset.wardrobeCatalogProduct;
    if (catalogCards.has(handle)) card.remove();
    else catalogCards.set(handle, card);
  });
  catalogItems.forEach(item => {
    const card = catalogCards.get(item.handle);
    if (card) catalogGrid.append(card);
  });
  function selectCategory(handle = 'all') {
    const selected = categoryButtons.find(button => button.dataset.wardrobeCategory === handle);
    if (handle !== 'all' && !selected) return;
    categoryButtons.forEach(button => button.setAttribute('aria-pressed', String(button.dataset.wardrobeCategory === handle)));
    let shown = 0;
    catalogCards.forEach((card, productHandle) => {
      card.hidden = handle !== 'all' && !categoryProducts.get(handle)?.has(productHandle);
      if (!card.hidden) shown++;
    });
    if (categoryEmpty) categoryEmpty.hidden = shown > 0;
    if (categoryLink) {
      categoryLink.href = selected?.dataset.categoryUrl || categoryLink.dataset.allUrl || '/collections/all';
      categoryLink.textContent = handle === 'all' ? 'Open the complete collection page ↗' : `Open ${selected.textContent.trim()} collection ↗`;
    }
    selected?.scrollIntoView?.({block: 'nearest', inline: 'nearest', behavior: 'smooth'});
  }
  categoryButtons.forEach(button => button.addEventListener('click', () => selectCategory(button.dataset.wardrobeCategory)));
  $('[data-wardrobe-category-prev]', catalogPanel)?.addEventListener('click', () => categoryTrack?.scrollBy({left: -180, behavior: 'smooth'}));
  $('[data-wardrobe-category-next]', catalogPanel)?.addEventListener('click', () => categoryTrack?.scrollBy({left: 180, behavior: 'smooth'}));
  const inspectorTitle = $('[data-wardrobe-title]', inspector);
  const inspectorPrice = $('[data-wardrobe-price]', inspector);
  const inspectorPhoto = $('[data-wardrobe-photo]', inspector);
  const inspectorPhotoCount = $('[data-wardrobe-photo-count]', inspector);
  const inspectorDescription = $('[data-wardrobe-description]', inspector);
  const actualLink = $('[data-wardrobe-actual]', inspector);

  const prevButton = $('[data-wardrobe-prev]', viewport);
  const nextButton = $('[data-wardrobe-next]', viewport);
  const groupCounter = $('[data-wardrobe-counter]', viewport);
  const chapterEl = $('[data-wardrobe-chapter]', viewport);
  const captionEl = $('[data-wardrobe-caption]', viewport);

  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches || document.documentElement.dataset.motion === 'off';
  const localeRoot = ((window.Shopify && Shopify.routes && Shopify.routes.root) || window.Rangnoraa?.root || '/').replace(/\/?$/, '/');
  const currency = window.Rangnoraa?.currency || 'INR';
  const money = amount => new Intl.NumberFormat(document.documentElement.lang || 'en-IN', {
    style: 'currency', currency, maximumFractionDigits: 2
  }).format((Number(amount) || 0) / 100);

  const hydration = new Map();
  let sceneController = null;
  let disposed = false;
  let centerIndex = ordered.length ? 0 : (catalogItems.length > 1 ? 1 : 0);
  let selectedSlot = 1;
  let focused = false;
  let photoIndex = 0;
  let renderToken = 0;
  let stageStep = -1;
  let gestureStartY = null;
  let gestureStartX = null;
  let carouselLockUntil = 0;
  let lastJourneyProgress = null;
  let pinchStartDistance = null;
  let pinchStartZoom = 1;
  let pinchZoom = 1;
  let wasPinching = false;

  function wrapIndex(index) {
    const length = catalogItems.length;
    if (!length) return -1;
    return ((index % length) + length) % length;
  }
  function trioAtCenter(index = centerIndex) {
    if (!catalogItems.length) return [null, null, null];
    if (catalogItems.length === 1) return [null, catalogItems[0], null];
    if (catalogItems.length === 2) return [catalogItems[wrapIndex(index - 1)], catalogItems[wrapIndex(index)], null];
    return [
      catalogItems[wrapIndex(index - 1)],
      catalogItems[wrapIndex(index)],
      catalogItems[wrapIndex(index + 1)]
    ];
  }
  function activeGroup() { return trioAtCenter(centerIndex); }
  function activeProduct() { return activeGroup()[1] || activeGroup().find(Boolean) || null; }
  function preloadAround(index) {
    if (!catalogItems.length) return;
    for (let offset = -2; offset <= 2; offset++) {
      const item = catalogItems[wrapIndex(index + offset)];
      const source = item?.cutout || item?.image;
      if (!source) continue;
      const image = new Image();
      image.decoding = 'async';
      image.src = source;
    }
  }

  function normalizeRemoteProduct(base, payload) {
    const variants = (payload.variants || []).map(variant => {
      const options = Array.isArray(variant.options)
        ? variant.options
        : [variant.option1, variant.option2, variant.option3].filter(value => value !== null && value !== undefined);
      return {...variant, id: Number(variant.id), price: Number(variant.price || 0), available: Boolean(variant.available), options};
    });
    const optionNames = (payload.options || []).map(option => typeof option === 'string' ? option : option?.name).filter(Boolean);
    const options = optionNames.map((name, index) => ({
      name,
      values: [...new Set(variants.map(variant => variant.options[index]).filter(value => value !== undefined && value !== null && value !== ''))]
    }));
    Object.assign(base, {
      id: payload.id ?? base.id,
      handle: payload.handle || base.handle,
      title: payload.title || base.title,
      url: localeRoot + 'products/' + encodeURIComponent(payload.handle || base.handle),
      available: payload.available ?? variants.some(variant => variant.available),
      price: Number(payload.price ?? base.price ?? variants[0]?.price ?? 0),
      description: stripHTML(payload.description || base.description || ''),
      image: payload.featured_image || base.image || payload.images?.[0] || null,
      images: Array.isArray(payload.images) ? payload.images : (base.images || []),
      variants,
      options,
      _hydrated: true
    });
    return base;
  }

  async function hydrateProduct(item) {
    if (!item?.handle) return item;
    if (item._hydrated && Array.isArray(item.variants) && item.variants.length) return item;
    if (hydration.has(item.handle)) return hydration.get(item.handle);
    const request = fetch(localeRoot + 'products/' + encodeURIComponent(item.handle) + '.js', {
      headers: {Accept: 'application/json'}
    }).then(response => {
      if (!response.ok) throw new Error('Could not load sizes and colours.');
      return response.json();
    }).then(payload => normalizeRemoteProduct(item, payload)).finally(() => hydration.delete(item.handle));
    hydration.set(item.handle, request);
    return request;
  }

  function preferredVariant(item) {
    if (!item?.variants?.length) return null;
    return item.variants.find(variant => variant.available) || item.variants[0];
  }

  function currentQuickVariant(item = activeProduct()) {
    if (!item?.variants?.length) return null;
    const selectedValues = $$('select[data-wardrobe-option-index]', quickOptions)
      .sort((a, b) => Number(a.dataset.wardrobeOptionIndex) - Number(b.dataset.wardrobeOptionIndex))
      .map(select => select.value);
    if (!selectedValues.length) return preferredVariant(item);
    return item.variants.find(variant => selectedValues.every((value, index) => String(variant.options[index]) === String(value))) || null;
  }

  function renderQuickVariant(item = activeProduct()) {
    const variant = currentQuickVariant(item);
    if (variant) quickPrice.textContent = money(variant.price);
    quickVariant.value = variant?.id || '';
    const available = Boolean(variant?.available);
    quickBuy.disabled = !available;
    quickAdd.disabled = !available;
    quickBuy.firstChild.textContent = available ? 'Buy now ' : 'Unavailable ';
    quickStatus.textContent = available ? '' : (item ? 'This combination is currently unavailable.' : 'No product selected.');
  }

  function buildQuickOptions(item) {
    quickOptions.replaceChildren();
    const first = preferredVariant(item);
    (item?.options || []).forEach((option, optionIndex) => {
      if (!option?.values?.length) return;
      if (String(option.name).toLowerCase() === 'title' && option.values.length === 1) return;
      const field = document.createElement('label');
      field.className = 'wardrobe-quick-field';
      const label = document.createElement('span');
      const optionName = /colou?r/i.test(option.name) ? 'COLOUR' : String(option.name || 'OPTION').toUpperCase();
      label.textContent = optionName;
      const select = document.createElement('select');
      select.dataset.wardrobeOptionIndex = String(optionIndex);
      select.setAttribute('aria-label', optionName);
      option.values.forEach(value => {
        const optionEl = document.createElement('option');
        optionEl.value = value;
        optionEl.textContent = value;
        select.append(optionEl);
      });
      if (first?.options?.[optionIndex] !== undefined) select.value = first.options[optionIndex];
      select.addEventListener('change', () => renderQuickVariant(item));
      field.append(label, select);
      quickOptions.append(field);
    });
  }

  async function renderQuickProduct(item = activeProduct()) {
    const token = ++renderToken;
    productDetailsLink.href = item?.url || (item?.handle ? localeRoot + 'products/' + encodeURIComponent(item.handle) : localeRoot + 'collections/all');
    focusDetailsLink.href = productDetailsLink.href;
    quickTitle.textContent = item ? displayTitle(item.title) : 'Design unavailable';
    quickPrice.textContent = item ? money(item.price) : '';
    quickOptions.replaceChildren();
    quickVariant.value = '';
    quickBuy.disabled = true;
    quickAdd.disabled = true;
    quickStatus.textContent = '';
    if (!item) return;

    if (!item._hydrated || !item.variants?.length) {
      shop.classList.add('is-loading');
      quickStatus.textContent = 'Loading size and colour options…';
      try {
        await hydrateProduct(item);
      } catch (error) {
        if (token !== renderToken) return;
        quickStatus.textContent = 'Open Product More Details to choose this design.';
        shop.classList.remove('is-loading');
        return;
      }
    }
    if (token !== renderToken) return;
    shop.classList.remove('is-loading');
    quickStatus.textContent = '';
    focusDetailsLink.href = item.url || productDetailsLink.href;
    buildQuickOptions(item);
    renderQuickVariant(item);
  }

  function updateGroupUI() {
    const total = Math.max(catalogItems.length, 1);
    const current = catalogItems.length ? centerIndex + 1 : 0;
    groupCounter.textContent = String(current).padStart(2, '0') + ' / ' + String(total).padStart(2, '0');
    prevButton.disabled = catalogItems.length <= 1;
    nextButton.disabled = catalogItems.length <= 1;
    setLabel.textContent = catalogItems.length ? `LOOK ${String(current).padStart(2, '0')} · 3 ON STAGE` : 'NO LOOKS';
    preloadAround(centerIndex);
  }

  function updateFocusUI() {
    viewport.classList.toggle('is-focused', focused);
    focusPreview.setAttribute('aria-hidden', String(!focused));
    if (focused) {
      const item = activeProduct();
      const source = item?.cutout || item?.image || '';
      if (focusImage.getAttribute('src') !== source) {
        if (source) focusImage.src = source;
        else focusImage.removeAttribute('src');
      }
      focusImage.alt = item ? `Complete view of ${displayTitle(item.title)}` : '';
      focusDetailsLink.href = item?.url || (item?.handle ? localeRoot + 'products/' + encodeURIComponent(item.handle) : localeRoot + 'collections/all');
      focusPreview.classList.toggle('has-cutout', Boolean(item?.cutout));
    }
  }

  function resetProductZoom() {
    pinchZoom = 1;
    pinchStartZoom = 1;
    pinchStartDistance = null;
    focusPreview.style.setProperty('--wardrobe-image-zoom', '1');
    focusPreview.style.setProperty('--wardrobe-image-origin', '50% 50%');
    sceneController?.setPinchZoom?.(1);
  }

  function setImageZoomOrigin(clientX, clientY) {
    const bounds = focusImage.getBoundingClientRect();
    if (!bounds.width || !bounds.height) return;
    const x = clamp((clientX - bounds.left) / bounds.width * 100, 0, 100);
    const y = clamp((clientY - bounds.top) / bounds.height * 100, 0, 100);
    focusPreview.style.setProperty('--wardrobe-image-origin', `${x}% ${y}%`);
  }

  function setImageZoom(value) {
    pinchZoom = clamp(value, 1, 1.8);
    focusPreview.style.setProperty('--wardrobe-image-zoom', String(pinchZoom));
  }

  function showAllThree() {
    focused = false;
    selectedSlot = 1;
    resetProductZoom();
    updateFocusUI();
    sceneController?.focus(1, false);
    chapterEl.textContent = '02 / BROWSE';
    captionEl.textContent = 'Three designs together. Tap a side look to bring it to the centre.';
  }

  function highlightCenter() {
    if (!viewport.classList.contains('has-products') || !activeProduct()) return;
    selectedSlot = 1;
    focused = true;
    updateFocusUI();
    sceneController?.focus(1, true);
    renderQuickProduct(activeProduct());
    chapterEl.textContent = '03 / HIGHLIGHT';
    captionEl.textContent = 'See the complete look up close. Swipe up to return to all three.';
  }

  function moveCarousel(delta) {
    if (!catalogItems.length || catalogItems.length === 1 || Date.now() < carouselLockUntil) return;
    const direction = delta >= 0 ? 1 : -1;
    carouselLockUntil = Date.now() + (reduced ? 140 : 720);
    viewport.classList.add('is-group-changing');
    window.setTimeout(() => viewport.classList.remove('is-group-changing'), reduced ? 160 : 700);

    showAllThree();
    centerIndex = wrapIndex(centerIndex + direction);
    updateGroupUI();
    sceneController?.setGroup(activeGroup(), direction, false);
    renderQuickProduct(activeProduct());
    chapterEl.textContent = '02 / BROWSE';
    captionEl.textContent = direction > 0
      ? 'The wardrobe slides left and one new design enters from the right.'
      : 'The wardrobe slides right and the previous design returns from the left.';
  }

  function handleLookClick(slot) {
    if (slot === 1) {
      highlightCenter();
      return;
    }
    moveCarousel(slot === 0 ? -1 : 1);
  }

  function goToCatalogIndex(index, focusItem = true) {
    if (!Number.isInteger(index) || index < 0 || index >= catalogItems.length) return;
    const direction = index === centerIndex ? 1 : (index > centerIndex ? 1 : -1);
    centerIndex = index;
    selectedSlot = 1;
    focused = Boolean(focusItem);
    resetProductZoom();
    updateGroupUI();
    updateFocusUI();
    sceneController?.setGroup(activeGroup(), direction, true);
    sceneController?.focus(1, focused);
    renderQuickProduct(activeProduct());
  }

  async function addSelectedProduct(goDirectToCheckout) {
    const item = activeProduct();
    if (!item) return;
    if (!item._hydrated) {
      try { await hydrateProduct(item); buildQuickOptions(item); } catch (error) {
        quickStatus.textContent = error.message;
        return;
      }
    }
    const variant = currentQuickVariant(item);
    if (!variant?.available) {
      quickStatus.textContent = 'Please choose an available size and colour.';
      renderQuickVariant(item);
      return;
    }
    const quantity = Math.max(1, Number(quickQuantity.value) || 1);
    quickBuy.disabled = true;
    quickAdd.disabled = true;
    quickStatus.textContent = goDirectToCheckout ? 'Preparing checkout…' : 'Adding to bag…';
    try {
      const formData = new FormData();
      formData.append('id', String(variant.id));
      formData.append('quantity', String(quantity));
      const response = await fetch(localeRoot + 'cart/add.js', {
        method: 'POST',
        headers: {Accept: 'application/json'},
        body: formData
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'We could not add this design.');
      if (goDirectToCheckout) {
        location.assign('/checkout');
        return;
      }
      quickStatus.textContent = 'Added to your bag.';
      const cartButton = document.querySelector('[data-cart-open]');
      if (cartButton && window.Rangnoraa?.drawer) cartButton.click();
    } catch (error) {
      quickStatus.textContent = error.message;
    } finally {
      if (!goDirectToCheckout) renderQuickVariant(item);
    }
  }

  function showPhoto(index) {
    const item = activeProduct();
    const photos = item?.images?.length ? item.images : (item?.image ? [item.image] : []);
    photoIndex = photos.length ? (index + photos.length) % photos.length : 0;
    if (photos[photoIndex]) inspectorPhoto.src = photos[photoIndex];
    else inspectorPhoto.removeAttribute('src');
    inspectorPhoto.alt = item ? `${displayTitle(item.title)}, product photo ${photoIndex + 1}` : '';
    inspectorPhotoCount.textContent = String(photoIndex + 1).padStart(2, '0') + ' / ' + String(Math.max(photos.length, 1)).padStart(2, '0');
    $('[data-wardrobe-photo-prev]', inspector).disabled = photos.length < 2;
    $('[data-wardrobe-photo-next]', inspector).disabled = photos.length < 2;
  }

  async function renderInspector() {
    const item = activeProduct();
    if (!item) return;
    inspectorTitle.textContent = displayTitle(item.title);
    inspectorPrice.textContent = money(item.price);
    inspectorDescription.textContent = item.description || 'Explore the product photographs for exact colour, fabric detail and fit.';
    actualLink.href = item.url || (localeRoot + 'products/' + encodeURIComponent(item.handle));
    showPhoto(0);
    try {
      await hydrateProduct(item);
      if (item !== activeProduct()) return;
      inspectorTitle.textContent = displayTitle(item.title);
      inspectorPrice.textContent = money(currentQuickVariant(item)?.price ?? item.price);
      inspectorDescription.textContent = item.description || 'Explore the product photographs for exact colour, fabric detail and fit.';
      actualLink.href = item.url;
      showPhoto(0);
    } catch (_) { /* The catalog link remains usable even if product JSON is unavailable. */ }
  }

  function closePanels() {
    inspector.hidden = true;
    catalogPanel.hidden = true;
    viewport.classList.remove('is-inspecting', 'is-catalog-open');
  }

  function openInspector() {
    catalogPanel.hidden = true;
    viewport.classList.remove('is-catalog-open');
    if (!focused) highlightCenter();
    renderInspector();
    inspector.hidden = false;
    viewport.classList.add('is-inspecting');
    $('[data-wardrobe-inspector-close]', inspector)?.focus({preventScroll: true});
  }

  function openCatalog() {
    inspector.hidden = true;
    viewport.classList.remove('is-inspecting');
    catalogPanel.hidden = false;
    viewport.classList.add('is-catalog-open');
    $('[data-wardrobe-catalog-close]', catalogPanel)?.focus({preventScroll: true});
  }

  const chapter = ['01 / ENTER', '02 / BROWSE', '03 / DISCOVER', '04 / BROWSE'];
  const caption = [
    'Swipe up to explore the collection.',
    'Three designs share the room.',
    'Choose a design to bring it closer.',
    'Three designs remain in view. Select a side look to bring it to the centre.'
  ];
  function setStage(index) {
    const next = clamp(index, 0, 3);
    if (stageStep === next) return;
    stageStep = next;
    viewport.dataset.step = String(next);
    if (!focused) {
      chapterEl.textContent = chapter[next];
      captionEl.textContent = caption[next];
    }
  }
  function journeyProgress() {
    const roomHeight = root.offsetHeight - window.innerHeight;
    return roomHeight > 0 ? clamp(-root.getBoundingClientRect().top / roomHeight, 0, 1) : 0;
  }
  const enterCue = $('[data-wardrobe-enter-cue]', viewport);
  const productStop = $('[data-wardrobe-product-stop]', root);
  const cueKey = 'rangnoraa-wardrobe-explored-v1';
  let cueDismissed = false;
  try { cueDismissed = window.localStorage?.getItem(cueKey) === '1'; } catch (_) { /* Storage can be unavailable in private browsing. */ }
  document.documentElement.classList.add('wardrobe-snap-enabled');
  function dismissCue() {
    cueDismissed = true;
    if (enterCue) enterCue.hidden = true;
    try { window.localStorage?.setItem(cueKey, '1'); } catch (_) { /* The current visit still hides the cue. */ }
  }
  enterCue?.addEventListener('click', () => {
    dismissCue();
    productStop?.scrollIntoView({behavior: reduced ? 'instant' : 'smooth', block: 'start'});
  });
  function onScroll() {
    const progress = journeyProgress();
    const browsing = progress > .025;
    if (browsing && !cueDismissed) dismissCue();
    if (enterCue) enterCue.hidden = cueDismissed || browsing;
    /* A normal upward page scroll also closes the enlarged product. */
    if (focused && (!browsing || (lastJourneyProgress !== null && progress > lastJourneyProgress + .015))) showAllThree();
    lastJourneyProgress = progress;
    viewport.classList.toggle('has-products', browsing);
    if (focused) return;
    setStage(browsing ? Math.max(1, Math.round(progress * 3)) : 0);
    sceneController?.setProgress(browsing ? Math.max(1, progress * 3) : 0);
  }

  prevButton.addEventListener('click', event => { event.preventDefault(); moveCarousel(-1); });
  nextButton.addEventListener('click', event => { event.preventDefault(); moveCarousel(1); });
  $('[data-wardrobe-catalog-open]', viewport).addEventListener('click', openCatalog);
  $('[data-wardrobe-catalog-close]', viewport).addEventListener('click', closePanels);
  $('[data-wardrobe-inspector-close]', viewport).addEventListener('click', closePanels);
  $('[data-wardrobe-focus-back]', focusPreview).addEventListener('click', () => {
    showAllThree();
    onScroll();
  });
  $('[data-wardrobe-rotate]', viewport).addEventListener('click', () => sceneController?.nudge(selectedSlot));
  $('[data-wardrobe-photo-prev]', viewport).addEventListener('click', () => showPhoto(photoIndex - 1));
  $('[data-wardrobe-photo-next]', viewport).addEventListener('click', () => showPhoto(photoIndex + 1));

  $('[data-wardrobe-quick-form]', shop).addEventListener('submit', event => {
    event.preventDefault();
    addSelectedProduct(false);
  });
  quickBuy.addEventListener('click', () => addSelectedProduct(true));

  $$('[data-wardrobe-catalog-product]', catalogPanel).forEach(button => button.addEventListener('click', () => {
    const index = catalogItems.findIndex(item => item?.handle === button.dataset.wardrobeCatalogProduct);
    if (index < 0) return;
    closePanels();
    goToCatalogIndex(index, true);
  }));

  viewport.addEventListener('keydown', event => {
    if (event.key === 'Escape') {
      if (!inspector.hidden || !catalogPanel.hidden) closePanels();
      else if (focused) {
        showAllThree();
        onScroll();
      }
      return;
    }
    if (event.target.closest('button, a, select, input, summary')) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
      event.preventDefault();
      moveCarousel(event.key === 'ArrowRight' ? 1 : -1);
    }
  });

  function touchDistance(touches) {
    if (!touches || touches.length < 2) return 0;
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  }

  viewport.addEventListener('touchstart', event => {
    if (!viewport.classList.contains('has-products')) return;
    if (event.touches.length >= 2) {
      wasPinching = true;
      pinchStartDistance = touchDistance(event.touches);
      pinchStartZoom = pinchZoom;
      gestureStartY = null;
      gestureStartX = null;
      highlightCenter();
      setImageZoomOrigin((event.touches[0].clientX + event.touches[1].clientX) / 2,
        (event.touches[0].clientY + event.touches[1].clientY) / 2);
      if (event.cancelable) event.preventDefault();
      return;
    }
    wasPinching = false;
    if (!canvasHost.contains(event.target) && event.target !== canvasHost && !focusPreview.contains(event.target)) return;
    gestureStartY = event.touches[0]?.clientY ?? null;
    gestureStartX = event.touches[0]?.clientX ?? null;
  }, {passive: false});

  viewport.addEventListener('touchmove', event => {
    if (event.touches.length >= 2 && pinchStartDistance) {
      if (event.cancelable) event.preventDefault();
      const distance = touchDistance(event.touches);
      if (!distance) return;
      setImageZoom(pinchStartZoom * (distance / pinchStartDistance));
      return;
    }
    if (gestureStartY !== null && event.touches.length === 1) {
      const dx = event.touches[0].clientX - gestureStartX;
      const dy = event.touches[0].clientY - gestureStartY;
      /* Let an ordinary vertical swipe move the page; the first swipe on an enlarged look closes it. */
      if (event.cancelable && focused && dy < -12 && Math.abs(dy) > Math.abs(dx) * 1.2) event.preventDefault();
    }
  }, {passive: false});

  viewport.addEventListener('touchend', event => {
    if (wasPinching || pinchStartDistance !== null) {
      if (event.touches.length < 2) {
        pinchStartDistance = null;
        pinchStartZoom = pinchZoom;
        wasPinching = false;
      }
      gestureStartY = null;
      gestureStartX = null;
      return;
    }
    if (gestureStartY === null) return;
    const endTouch = event.changedTouches[0];
    const endY = endTouch?.clientY ?? gestureStartY;
    const endX = endTouch?.clientX ?? gestureStartX ?? 0;
    const deltaY = endY - gestureStartY;
    const deltaX = endX - (gestureStartX ?? endX);
    gestureStartY = null;
    gestureStartX = null;
    if (Math.abs(deltaX) > 55 && Math.abs(deltaX) > Math.abs(deltaY) * 1.2) {
      moveCarousel(deltaX < 0 ? 1 : -1);
    } else if (deltaY < -55 && Math.abs(deltaY) > Math.abs(deltaX) * 1.2 && focused) {
      showAllThree();
      onScroll();
    }
  }, {passive: true});

  /* Trackpad pinch uses ctrl+wheel in Chromium; Safari also dispatches gesturechange. */
  viewport.addEventListener('wheel', event => {
    if (!event.ctrlKey || !sceneController || !viewport.classList.contains('has-products')) return;
    event.preventDefault();
    if (!focused) highlightCenter();
    setImageZoomOrigin(event.clientX, event.clientY);
    setImageZoom(pinchZoom * Math.exp(-event.deltaY * .012));
  }, {passive: false});
  let safariGestureStartZoom = 1;
  viewport.addEventListener('gesturestart', event => {
    if (!sceneController || !viewport.classList.contains('has-products')) return;
    event.preventDefault();
    safariGestureStartZoom = pinchZoom;
    highlightCenter();
    setImageZoomOrigin(event.clientX, event.clientY);
  }, {passive: false});
  viewport.addEventListener('gesturechange', event => {
    if (!sceneController || !viewport.classList.contains('has-products')) return;
    event.preventDefault();
    setImageZoom(safariGestureStartZoom * event.scale);
  }, {passive: false});
  viewport.addEventListener('gestureend', event => {
    if (sceneController) event.preventDefault();
  }, {passive: false});

  window.addEventListener('scroll', onScroll, {passive: true});
  window.addEventListener('resize', onScroll, {passive: true});

  updateGroupUI();
  updateFocusUI();
  renderQuickProduct(activeProduct());
  onScroll();

  const instance = {
    dispose() {
      disposed = true;
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      document.documentElement.classList.remove('wardrobe-snap-enabled');
      sceneController?.dispose();
      instances.delete(root);
    }
  };
  instances.set(root, instance);

  if (!window.WebGLRenderingContext || !root.dataset.threeUrl) return;

  import(root.dataset.threeUrl).then(THREE => {
    if (disposed) return;
    sceneController = createRoom(THREE, canvasHost, viewport, activeGroup(), reduced, {
      onLookClick(slot) { handleLookClick(slot); },
      onSwipe(direction) { moveCarousel(direction); },
      isPinching() { return wasPinching || pinchStartDistance !== null; },
      onBackgroundClick() {
        if (!focused) return;
        showAllThree();
        onScroll();
      }
    });
    if (!sceneController) return;
    sceneController.setProgress(viewport.classList.contains('has-products') ? Math.max(1, journeyProgress() * 3) : 0);
  }).catch(() => {
    /* The quick-buy rail, catalog and product details remain fully usable without WebGL. */
  });
}

function createRoom(T, host, viewport, initialItems, reduced, callbacks = {}) {
  let renderer;
  try {
    renderer = new T.WebGLRenderer({antialias: true, alpha: false, powerPreference: 'high-performance'});
  } catch (_) { return null; }
  host.append(renderer.domElement);
  renderer.outputColorSpace = T.SRGBColorSpace;
  renderer.toneMapping = T.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.37;
  renderer.setPixelRatio(Math.min(devicePixelRatio || 1, innerWidth < 761 ? 1.5 : 1.75));
  renderer.domElement.style.touchAction = 'pan-y';
  const scene = new T.Scene();
  scene.background = new T.Color('#f3e9da');
  scene.fog = new T.Fog('#f3e9da', 28, 48);
  const camera = new T.PerspectiveCamera(54, 1, 0.1, 80);
  const cameraAim = new T.Vector3(0, 2.15, -3.4);
  const cameraTarget = new T.Vector3(0, 2.15, -3.4);
  const positionTarget = new T.Vector3(0, 2.8, 10);
  camera.position.copy(positionTarget); camera.lookAt(cameraAim);
  scene.add(new T.HemisphereLight(0xffffff, 0xc4a887, 3.3));
  const sun = new T.DirectionalLight(0xfff1d7, 3.4); sun.position.set(-3.8, 9, 5); scene.add(sun);
  const fill = new T.PointLight(0xffffff, 65, 23); fill.position.set(3, 5.5, -2); scene.add(fill);
  const cream = new T.MeshStandardMaterial({color: '#f1e8d9', roughness: 0.92, side: T.DoubleSide});
  const plaster = new T.MeshStandardMaterial({color: '#efe4d3', roughness: 0.96});
  const brass = new T.MeshStandardMaterial({color: '#a37a38', metalness: 0.72, roughness: 0.28});
  const walnut = new T.MeshStandardMaterial({color: '#805837', roughness: 0.53});
  const ivory = new T.MeshStandardMaterial({color: '#faf5e8', roughness: 0.84, side: T.DoubleSide});
  const soft = new T.MeshStandardMaterial({color: '#d8cab3', roughness: 0.93});
  const add = (geo, mat, x, y, z, group = scene) => {const mesh = new T.Mesh(geo, mat); mesh.position.set(x,y,z); group.add(mesh); return mesh;};
  const tube = (points, radius, material, group = scene, closed = false) => {
    const curve = new T.CatmullRomCurve3(points.map(p => new T.Vector3(...p)), closed);
    const mesh = new T.Mesh(new T.TubeGeometry(curve, 90, radius, 6, closed), material);
    group.add(mesh); return mesh;
  };

  function woodTexture() {
    const canvas = document.createElement('canvas'); canvas.width = 512; canvas.height = 512;
    const ctx = canvas.getContext('2d'); ctx.fillStyle = '#aa855d'; ctx.fillRect(0,0,512,512);
    for (let row = 0; row < 8; row++) {
      const y = row * 64; ctx.fillStyle = row % 2 ? '#ae8b66' : '#bc9870'; ctx.fillRect(0,y,512,62);
      ctx.fillStyle = '#664a321f'; ctx.fillRect(0,y + 61,512,3);
      for (let line = 0; line < 17; line++) {
        const offset = ((line * 71 + row * 91) % 512);
        ctx.strokeStyle = line % 3 ? '#ffffff12' : '#664c3822'; ctx.lineWidth = 1 + (line % 2);
        ctx.beginPath(); ctx.moveTo(0,y + (offset % 60));
        ctx.bezierCurveTo(170,y + (offset % 60) + 4,340,y + (offset % 60) - 3,512,y + (offset % 60) + 2);ctx.stroke();
      }
    }
    const texture = new T.CanvasTexture(canvas); texture.colorSpace = T.SRGBColorSpace;
    texture.wrapS = texture.wrapT = T.RepeatWrapping; texture.repeat.set(5, 9);
    texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 8);
    return texture;
  }
  const floor = add(new T.PlaneGeometry(24, 39), new T.MeshStandardMaterial({map: woodTexture(), roughness: .62, metalness: .06}), 0, -.015, -2);
  floor.rotation.x = -Math.PI / 2;
  const ceiling = add(new T.PlaneGeometry(24, 39), plaster, 0, 7.6, -2); ceiling.rotation.x = Math.PI / 2;
  add(new T.BoxGeometry(.32, 7.8, 38), plaster, -9, 3.8, -2);
  add(new T.BoxGeometry(.32, 7.8, 38), plaster, 9, 3.8, -2);
  add(new T.BoxGeometry(18.4, 7.8, .4), plaster, 0, 3.8, -21);
  for (const x of [-8.7, 8.7]) {
    for (const z of [-12, -3, 6]) {
      const column = add(new T.CylinderGeometry(.72,.72,7.5,24), cream, x,3.75,z);
      column.scale.z = .81;
      add(new T.CylinderGeometry(.75,.75,.035,24), brass,x, .15,z);
      add(new T.CylinderGeometry(.75,.75,.035,24), brass,x,7.05,z);
    }
  }
  const floorTrack = [[0,.04,12],[.4,.04,7],[1.9,.04,3],[-.6,.04,-.8],[-2.5,.04,-4.9],[0,.04,-8],[4.3,.04,-10]];
  tube(floorTrack, .035, brass);
  tube([[-.85,.045,9],[1,.045,5],[3,.045,1],[2,.045,-2],[-1,.045,-4]], .012, new T.MeshBasicMaterial({color:'#ddba70'}));

  // The same roof opening and silk remain in place while the camera moves.
  const oculus = add(new T.CircleGeometry(3.1, 64), new T.MeshBasicMaterial({color:'#c9e2f0',side:T.DoubleSide}), 0,7.565,-3.6);
  oculus.rotation.x = Math.PI / 2; oculus.scale.y = 1.43;
  const ellipse = Array.from({length:64}, (_,i) => {
    const a = i / 64 * Math.PI * 2; return [Math.cos(a)*3.12,7.49,-3.6 + Math.sin(a)*4.45];
  });
  tube(ellipse,.10,brass,scene,true);
  for (let strip = 0; strip < 4; strip++) {
    const geo = new T.BufferGeometry(), verts = [], uvs = [], idx = [];
    const width = 1.12 + strip * .17;
    for (let i = 0; i <= 42; i++) {
      const t = i / 42, z = 8 - t * 22;
      const mid = Math.sin(t * Math.PI * 2 + strip * .88) * (1.1 + strip * .3);
      const drop = Math.sin(t * Math.PI * 4 + strip * 1.23) * .47;
      for (let edge = 0; edge < 2; edge++) {
        verts.push(mid + (edge - .5)*width, 6.4 + drop + Math.cos(edge * 3 + t * 10)*.13, z);
        uvs.push(edge,t);
      }
      if (i < 42) {const n=i*2;idx.push(n,n+2,n+1,n+1,n+2,n+3);}
    }
    geo.setAttribute('position',new T.Float32BufferAttribute(verts,3));geo.setAttribute('uv',new T.Float32BufferAttribute(uvs,2));geo.setIndex(idx);geo.computeVertexNormals();
    const fabric = new T.MeshPhysicalMaterial({color:'#fff8e9',side:T.DoubleSide,roughness:.76,transparent:true,opacity:.79,depthWrite:false});
    scene.add(new T.Mesh(geo,fabric));
  }

  // Bronze-framed mirror at the rear of this single room.
  const mirror = add(new T.PlaneGeometry(3.6,5.7), new T.MeshPhysicalMaterial({color:'#d9ceb9',metalness:.68,roughness:.22,side:T.DoubleSide}), -4.8,3.2,-20.73);
  const arch = Array.from({length:37},(_,i)=>{const a=Math.PI*i/36;return [-4.8+1.86*Math.cos(a),3.2+2.94*Math.sin(a),-20.6];});
  tube([[-6.66,.27,-20.6],[-6.66,3.2,-20.6],...arch,[-2.94,3.2,-20.6],[-2.94,.27,-20.6]], .065, brass);
  add(new T.PlaneGeometry(3.35,5.4), new T.MeshBasicMaterial({color:'#ffffff',transparent:true,opacity:.12,side:T.DoubleSide}),-4.8,3.2,-20.55);
  mirror.rotation.y = 0;

  function rail(x,z,rotation) {
    const group = new T.Group(); group.position.set(x,0,z);group.rotation.y=rotation;scene.add(group);
    for (const side of [-1,1]) {
      add(new T.CylinderGeometry(.026,.026,2.8,8),brass,side*1.9,1.5,0,group);
      add(new T.CylinderGeometry(.12,.12,.035,12),brass,side*1.9,.08,0,group);
    }
    const bar = add(new T.CylinderGeometry(.035,.035,3.86,8),brass,0,2.93,0,group); bar.rotation.z=Math.PI/2;
    for (let i=0;i<11;i++) {
      const x0=-1.62+i*.325;
      add(new T.TorusGeometry(.055,.012,5,11,Math.PI),brass,x0,2.84,0,group);
      const hanger = new T.Mesh(new T.ConeGeometry(.32,.24,3),new T.MeshStandardMaterial({color:'#ad8559',roughness:.7}));
      hanger.position.set(x0,2.69,.01);hanger.rotation.z=Math.PI;group.add(hanger);
      const tone=['#ede2c8','#9cb9d8','#d6bc8b','#a5aa87','#eee0d7'][i%5];
      const garment = add(new T.PlaneGeometry(.43,1.22),new T.MeshStandardMaterial({color:tone,side:T.DoubleSide,roughness:.9}),x0,1.92,.02,group);
      garment.rotation.y=.12*(i%3-1);
    }
  }
  rail(6.5,-7,-.14);rail(-6.4,1,.18);rail(6.4,8,-.1);


  /* Visual slots are LEFT / CENTRE / RIGHT and shifted slightly left to reserve clean space for the purchase rail. */
  let narrow = host.clientWidth < 761;
  const homePositions = narrow ? [
    new T.Vector3(-2.75, 0, -5.3),
    new T.Vector3(0, 0, -3.35),
    new T.Vector3(2.75, 0, -5.3)
  ] : [
    new T.Vector3(-4.0, 0, -5.9),
    new T.Vector3(-0.45, 0, -3.35),
    new T.Vector3(3.05, 0, -5.9)
  ];
  const textureLoader = new T.TextureLoader();
  textureLoader.setCrossOrigin('anonymous');

  function normalizedCatalogTexture(sourceTexture) {
    const image = sourceTexture?.image;
    if (!image) return sourceTexture;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 720;
      canvas.height = 1040;
      const ctx = canvas.getContext('2d', {alpha: true});
      if (!ctx) return sourceTexture;

      const bg = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      bg.addColorStop(0, '#f7efe3');
      bg.addColorStop(.58, '#eee0cc');
      bg.addColorStop(1, '#e4d2b9');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const iw = Number(image.naturalWidth || image.videoWidth || image.width || 1);
      const ih = Number(image.naturalHeight || image.videoHeight || image.height || 1);
      const inset = 38;
      const fit = Math.min((canvas.width - inset * 2) / iw, (canvas.height - inset * 2) / ih);
      const dw = iw * fit;
      const dh = ih * fit;
      const dx = (canvas.width - dw) * .5;
      const dy = (canvas.height - dh) * .5;
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.globalAlpha = .98;
      ctx.filter = 'saturate(1.03) contrast(1.015)';
      ctx.drawImage(image, dx, dy, dw, dh);
      ctx.restore();

      /* Fade the photographic rectangle into the room so mixed catalog photography does not look pasted on. */
      ctx.globalCompositeOperation = 'destination-in';
      let mask = ctx.createLinearGradient(0, 0, canvas.width, 0);
      mask.addColorStop(0, 'rgba(255,255,255,0)');
      mask.addColorStop(.075, 'rgba(255,255,255,1)');
      mask.addColorStop(.925, 'rgba(255,255,255,1)');
      mask.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = mask;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      mask = ctx.createLinearGradient(0, 0, 0, canvas.height);
      mask.addColorStop(0, 'rgba(255,255,255,0)');
      mask.addColorStop(.055, 'rgba(255,255,255,1)');
      mask.addColorStop(.94, 'rgba(255,255,255,1)');
      mask.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = mask;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.globalCompositeOperation = 'source-over';

      const texture = new T.CanvasTexture(canvas);
      texture.colorSpace = T.SRGBColorSpace;
      texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 6);
      sourceTexture.dispose();
      return texture;
    } catch (_) {
      return sourceTexture;
    }
  }

  function curvedPortraitGeometry(width, height) {
    const geometry = new T.PlaneGeometry(width, height, 28, 40);
    const position = geometry.attributes.position;
    for (let i = 0; i < position.count; i++) {
      const x = position.getX(i);
      const y = position.getY(i);
      const nx = x / (width * .5);
      const ny = (y + height * .5) / height;
      const horizontal = Math.max(0, 1 - nx * nx);
      const torso = Math.sin(clamp(ny, 0, 1) * Math.PI);
      const depth = .045 + horizontal * (.07 + torso * .055);
      position.setZ(i, depth);
    }
    position.needsUpdate = true;
    geometry.computeVertexNormals();
    return geometry;
  }

  function makeLook(index) {
    const home = homePositions[index].clone();
    const base = new T.Group();
    base.position.copy(home);
    scene.add(base);

    const radius = index === 1 ? 1.65 : 1.32;
    const bottom = new T.Mesh(new T.CylinderGeometry(radius, radius, .28, 48), walnut);
    bottom.position.y = .16;
    bottom.userData.lookIndex = index;
    base.add(bottom);

    const rim = new T.Mesh(new T.CylinderGeometry(radius + .01, radius + .01, .04, 48), brass);
    rim.position.y = .085;
    base.add(rim);

    const top = new T.Mesh(new T.CylinderGeometry(radius, radius, .025, 48), walnut);
    top.position.y = .32;
    base.add(top);

    const portraitGroup = new T.Group();
    portraitGroup.position.y = .32;
    base.add(portraitGroup);

    const geometry = curvedPortraitGeometry(2.82, 4.17);
    const material = new T.MeshBasicMaterial({
      transparent: true,
      alphaTest: .025,
      depthWrite: true,
      side: T.DoubleSide,
      toneMapped: false,
      opacity: 0
    });
    const relief = new T.Mesh(geometry, material);
    relief.position.set(0, 2.1, .14);
    relief.userData.lookIndex = index;
    relief.visible = false;
    portraitGroup.add(relief);

    const holder = {
      index,
      home,
      base,
      bottom,
      portraitGroup,
      relief,
      material,
      item: null,
      loadToken: 0,
      loading: false,
      angleOffset: 0,
      visualOpacity: 1
    };

    holder.setProduct = async item => {
      holder.item = item || null;
      const source = item?.cutout || item?.image || null;
      const token = ++holder.loadToken;
      holder.loading = true;
      holder.relief.visible = false;
      holder.material.opacity = 0;
      if (!source) {
        holder.loading = false;
        if (holder.material.map) {
          holder.material.map.dispose();
          holder.material.map = null;
          holder.material.needsUpdate = true;
        }
        return;
      }
      try {
        let texture = await textureLoader.loadAsync(source);
        if (token !== holder.loadToken || !base.parent) {
          texture.dispose();
          return;
        }
        texture.colorSpace = T.SRGBColorSpace;
        texture.anisotropy = Math.min(renderer.capabilities.getMaxAnisotropy(), 6);
        const usesRealCutout = Boolean(item?.cutout);
        const imageWidth = Number(texture.image?.naturalWidth || texture.image?.videoWidth || texture.image?.width || 0);
        const imageHeight = Number(texture.image?.naturalHeight || texture.image?.videoHeight || texture.image?.height || 0);
        const imageAspect = imageWidth > 0 && imageHeight > 0 ? imageWidth / imageHeight : (2.82 / 4.17);
        if (!usesRealCutout) texture = normalizedCatalogTexture(texture);
        holder.relief.scale.x = usesRealCutout ? clamp(imageAspect / (2.82 / 4.17), .84, 1.18) : .94;
        holder.relief.scale.y = usesRealCutout ? 1 : .96;
        const previous = holder.material.map;
        holder.material.map = texture;
        holder.material.needsUpdate = true;
        if (previous) previous.dispose();
        holder.relief.visible = true;
      } catch (_) {
        if (token === holder.loadToken) holder.relief.visible = false;
      } finally {
        if (token === holder.loadToken) holder.loading = false;
      }
    };

    return holder;
  }

  const holders = [makeLook(0), makeLook(1), makeLook(2)];

  const shadowCanvas = document.createElement('canvas');
  shadowCanvas.width = 128;
  shadowCanvas.height = 128;
  const shadowContext = shadowCanvas.getContext('2d');
  const shadowGradient = shadowContext.createRadialGradient(64, 64, 5, 64, 64, 64);
  shadowGradient.addColorStop(0, '#29190c70');
  shadowGradient.addColorStop(1, '#29190c00');
  shadowContext.fillStyle = shadowGradient;
  shadowContext.fillRect(0, 0, 128, 128);
  const shadowMap = new T.CanvasTexture(shadowCanvas);
  holders.forEach((holder, index) => {
    const shade = new T.Mesh(
      new T.PlaneGeometry(index === 1 ? 5 : 4, index === 1 ? 5 : 4),
      new T.MeshBasicMaterial({map: shadowMap, transparent: true, depthWrite: false, side: T.DoubleSide})
    );
    shade.rotation.x = -Math.PI / 2;
    shade.position.y = .025;
    holder.base.add(shade);
  });

  for (const [x, z] of [[-7, -10], [7, -12], [-7, 4]]) {
    add(new T.CylinderGeometry(.43, .47, .8, 16), ivory, x, .42, z);
    const plant = new T.Group();
    plant.position.set(x, .8, z);
    scene.add(plant);
    for (let i = 0; i < 7; i++) {
      const a = i * 2.3999;
      const dx = Math.cos(a) * .62;
      const dz = Math.sin(a) * .62;
      tube([[0,0,0],[dx*.3,.7,dz*.3],[dx,1.55,dz]], .019, new T.MeshStandardMaterial({color:'#625b3b'}), plant);
      const leaf = add(new T.SphereGeometry(.12, 6, 5), new T.MeshStandardMaterial({color:i%2?'#777e55':'#57694a'}), dx, 1.45, dz, plant);
      leaf.scale.set(.55, 1.8, .35);
      leaf.rotation.z = -a * .23;
    }
  }

  let stage = 0;
  let chosen = 1;
  let visible = true;
  let frame = 0;
  let lastTime = 0;
  let stopped = false;
  let focusTarget = 0;
  let focusAmount = 0;
  let userZoomTarget = 1;
  let userZoom = 1;
  let transition = null;
  let currentItems = [null, null, null];
  let pointer = null;
  const zoomAnchor = new T.Vector3(0, 2.4, 0);

  const raycaster = new T.Raycaster();
  const pointerNdc = new T.Vector2();

  function smooth01(value) {
    const t = clamp(value, 0, 1);
    return t * t * (3 - 2 * t);
  }

  function offscreenPosition(side) {
    const edge = side < 0 ? homePositions[0] : homePositions[2];
    return new T.Vector3(edge.x + side * 4.2, edge.y, edge.z - 1.4);
  }

  function syncHolderSlots() {
    holders.forEach((holder, index) => {
      holder.index = index;
      holder.home.copy(homePositions[index]);
      holder.bottom.userData.lookIndex = index;
      holder.relief.userData.lookIndex = index;
      if (!transition) holder.base.position.copy(holder.home);
    });
  }

  function setGroup(items, direction = 1, immediate = false) {
    const nextItems = [...(items || [])].slice(0, 3);
    while (nextItems.length < 3) nextItems.push(null);
    focusTarget = 0;
    userZoomTarget = 1;
    chosen = 1;
    renderer.domElement.style.touchAction = 'pan-y';

    if (immediate || reduced || !currentItems.some(Boolean)) {
      transition = null;
      currentItems = nextItems;
      syncHolderSlots();
      holders.forEach((holder, index) => {
        holder.base.position.copy(holder.home);
        holder.base.scale.setScalar(1);
        holder.visualOpacity = 1;
        holder.setProduct(nextItems[index]);
      });
      return;
    }

    transition = {
      start: performance.now(),
      duration: 660,
      direction: direction >= 0 ? 1 : -1,
      nextItems,
      swapped: false
    };
  }

  function focus(slot, enabled = true) {
    chosen = 1;
    focusTarget = enabled ? 1 : 0;
    if (!enabled) userZoomTarget = 1;
    renderer.domElement.style.touchAction = enabled ? 'none' : 'pan-y';
  }

  function setPinchZoom(value) {
    chosen = 1;
    if (Number(value) > 1) focusTarget = 1;
    userZoomTarget = clamp(Number(value) || 1, 1, 1.8);
  }

  function setZoomAnchor(clientX, clientY) {
    const holder = holders[1];
    const bounds = renderer.domElement.getBoundingClientRect();
    if (!holder?.relief.visible || !bounds.width || !bounds.height) return;
    pointerNdc.set((clientX - bounds.left) / bounds.width * 2 - 1,
      -((clientY - bounds.top) / bounds.height * 2 - 1));
    raycaster.setFromCamera(pointerNdc, camera);
    const hit = raycaster.intersectObject(holder.relief, false)[0];
    if (hit) zoomAnchor.copy(hit.point).sub(holder.base.position).divideScalar(holder.base.scale.x || 1);
  }

  function nudge(slot) {
    const holder = holders[1];
    if (!holder) return;
    holder.angleOffset = holder.angleOffset >= 0 ? -.035 : .035;
  }

  function updateSlideTransition(time) {
    holders.forEach(holder => { holder.visualOpacity = 1; });
    if (!transition) {
      holders.forEach(holder => holder.base.position.copy(holder.home));
      return false;
    }

    const raw = clamp((time - transition.start) / transition.duration, 0, 1);
    const split = .48;
    const direction = transition.direction;

    if (raw < split) {
      const p = smooth01(raw / split);
      if (direction > 0) {
        holders[0].base.position.lerpVectors(homePositions[0], offscreenPosition(-1), p);
        holders[1].base.position.lerpVectors(homePositions[1], homePositions[0], p);
        holders[2].base.position.lerpVectors(homePositions[2], homePositions[1], p);
        holders[0].visualOpacity = 1 - p;
      } else {
        holders[2].base.position.lerpVectors(homePositions[2], offscreenPosition(1), p);
        holders[1].base.position.lerpVectors(homePositions[1], homePositions[2], p);
        holders[0].base.position.lerpVectors(homePositions[0], homePositions[1], p);
        holders[2].visualOpacity = 1 - p;
      }
      return true;
    }

    if (!transition.swapped) {
      transition.swapped = true;
      currentItems = transition.nextItems;
      if (direction > 0) {
        const incoming = holders.shift();
        holders.push(incoming);
        syncHolderSlots();
        incoming.base.position.copy(offscreenPosition(1));
        incoming.visualOpacity = 0;
        incoming.setProduct(currentItems[2]);
      } else {
        const incoming = holders.pop();
        holders.unshift(incoming);
        syncHolderSlots();
        incoming.base.position.copy(offscreenPosition(-1));
        incoming.visualOpacity = 0;
        incoming.setProduct(currentItems[0]);
      }
      /* The two retained holders already carry the two retained products. */
    }

    const p = smooth01((raw - split) / (1 - split));
    if (direction > 0) {
      holders[0].base.position.copy(homePositions[0]);
      holders[1].base.position.copy(homePositions[1]);
      holders[2].base.position.lerpVectors(offscreenPosition(1), homePositions[2], p);
      holders[2].visualOpacity = p;
    } else {
      holders[2].base.position.copy(homePositions[2]);
      holders[1].base.position.copy(homePositions[1]);
      holders[0].base.position.lerpVectors(offscreenPosition(-1), homePositions[0], p);
      holders[0].visualOpacity = p;
    }

    if (raw >= 1) {
      transition = null;
      syncHolderSlots();
    }
    return true;
  }

  function interpolatePose(progress) {
    const mobile = narrow;
    const poses = [
      {p:[mobile ? 0 : -.35,2.85,mobile ? 13.5 : 12.4],t:[mobile ? 0 : -.45,2.05,-3.8]},
      {p:[mobile ? 0 : 1.65,2.75,mobile ? 12.8 : 9.6],t:[mobile ? 0 : -.45,2.15,-3.6]},
      {p:[mobile ? 0 : .8,2.6,mobile ? 12 : 9.0],t:[mobile ? 0 : -.45,2.08,-3.6]},
      {p:[mobile ? 0 : -.35,2.85,mobile ? 11.8 : 10.5],t:[mobile ? 0 : -.45,2.15,-3.8]}
    ];
    const floorIndex = Math.min(2, Math.floor(progress));
    const v = clamp(progress - floorIndex, 0, 1);
    const s = v * v * (3 - 2 * v);
    const a = poses[floorIndex];
    const b = poses[floorIndex + 1];
    for (let i = 0; i < 3; i++) {
      positionTarget.setComponent(i, a.p[i] + (b.p[i] - a.p[i]) * s);
      cameraTarget.setComponent(i, a.t[i] + (b.t[i] - a.t[i]) * s);
    }
    /* Every scroll position stays in front of the three products. */
  }

  function resize() {
    const width = Math.max(1, host.clientWidth);
    const height = Math.max(1, host.clientHeight);
    if ((width < 761) !== narrow) {
      narrow = width < 761;
      const positions = narrow ? [[-2.75,0,-5.3],[0,0,-3.35],[2.75,0,-5.3]]
        : [[-4,0,-5.9],[-.45,0,-3.35],[3.05,0,-5.9]];
      homePositions.forEach((position, index) => position.set(...positions[index]));
      transition = null;
      syncHolderSlots();
    }
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.fov = width < 761 ? 58 : 52;
    camera.updateProjectionMatrix();
    if (reduced) draw(performance.now());
  }

  function draw(time) {
    const dt = clamp((time - lastTime) / 1000, 0, .08);
    lastTime = time;
    const focusEase = reduced ? 1 : 1 - Math.exp(-dt * 6.2);
    focusAmount += (focusTarget - focusAmount) * focusEase;
    userZoom += (userZoomTarget - userZoom) * (reduced ? 1 : 1 - Math.exp(-dt * 8));

    interpolatePose(stage);
    if (reduced) {
      camera.position.copy(positionTarget);
      cameraAim.copy(cameraTarget);
    } else {
      const eased = 1 - Math.exp(-dt * 3.3);
      camera.position.lerp(positionTarget, eased);
      cameraAim.lerp(cameraTarget, eased);
    }
    camera.lookAt(cameraAim);

    const sliding = updateSlideTransition(time);
    holders.forEach((holder, index) => {
      const isCentre = index === 1;
      if (!sliding) holder.base.position.copy(holder.home);
      const focusScale = isCentre ? 1.11 + .06 * focusAmount : .94;
      const emphasis = focusScale * (isCentre ? userZoom : 1);
      if (isCentre && !sliding && userZoom > 1) {
        /* Scale about the part touched by the customer, without moving the room or footer. */
        holder.base.position.addScaledVector(zoomAnchor, focusScale * (1 - userZoom));
      }
      holder.base.scale.setScalar(emphasis);

      const cameraAngle = Math.atan2(camera.position.x - holder.base.position.x, camera.position.z - holder.base.position.z);
      const sway = reduced ? 0 : Math.sin(time * .00031 + index) * .014;
      /* The enlarged photograph faces the viewer upright; the small side looks keep their subtle room angle. */
      holder.portraitGroup.rotation.y = isCentre
        ? cameraAngle * focusAmount + (1 - focusAmount) * clamp(cameraAngle * .11, -.055, .055)
        : clamp(cameraAngle * .11, -.055, .055) + holder.angleOffset + sway * .35;

      if (holder.relief.visible && !holder.loading) {
        holder.material.opacity = clamp(holder.visualOpacity, 0, 1) * (isCentre ? 1 - focusAmount : 1);
      } else {
        holder.material.opacity = 0;
      }
      holder.bottom.userData.lookIndex = index;
      holder.relief.userData.lookIndex = index;
    });

    renderer.render(scene, camera);
  }

  function loop(time) {
    frame = 0;
    if (stopped || !visible) return;
    draw(time);
    if (!reduced) frame = requestAnimationFrame(loop);
  }

  const observer = new IntersectionObserver(entries => {
    visible = Boolean(entries[0]?.isIntersecting) && !document.hidden;
    if (visible && !frame) frame = requestAnimationFrame(loop);
    if (!visible) {
      cancelAnimationFrame(frame);
      frame = 0;
    }
  }, {rootMargin:'180px 0px'});
  observer.observe(viewport);

  const onVisibility = () => {
    visible = !document.hidden;
    if (visible && !frame) frame = requestAnimationFrame(loop);
  };
  document.addEventListener('visibilitychange', onVisibility);

  const resizeObserver = new ResizeObserver(resize);
  resizeObserver.observe(host);
  resize();

  function onPointerDown(event) {
    if (!event.isPrimary) return;
    pointer = {x:event.clientX, y:event.clientY, lastX:event.clientX, moved:false};
  }
  function onPointerMove(event) {
    if (!pointer) return;
    const dxTotal = event.clientX - pointer.x;
    const dyTotal = event.clientY - pointer.y;
    if (Math.hypot(dxTotal, dyTotal) > 9) pointer.moved = true;
    pointer.lastX = event.clientX;
  }
  function onPointerUp(event) {
    if (callbacks.isPinching?.()) { pointer = null; return; }
    if (pointer?.moved && event.pointerType === 'mouse') {
      const dx = event.clientX - pointer.x;
      const dy = event.clientY - pointer.y;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy) * 1.2) callbacks.onSwipe?.(dx < 0 ? 1 : -1);
    }
    if (pointer && !pointer.moved) {
      const bounds = renderer.domElement.getBoundingClientRect();
      pointerNdc.set(
        (event.clientX - bounds.left) / bounds.width * 2 - 1,
        -((event.clientY - bounds.top) / bounds.height * 2 - 1)
      );
      raycaster.setFromCamera(pointerNdc, camera);
      const targets = holders.flatMap(holder => [holder.relief.visible ? holder.relief : null, holder.bottom].filter(Boolean));
      const hits = raycaster.intersectObjects(targets, true);
      if (hits.length) {
        const index = hits[0].object.userData.lookIndex;
        if (Number.isInteger(index)) callbacks.onLookClick?.(index);
      } else {
        callbacks.onBackgroundClick?.();
      }
    }
    pointer = null;
  }

  renderer.domElement.addEventListener('pointerdown', onPointerDown);
  renderer.domElement.addEventListener('pointermove', onPointerMove);
  renderer.domElement.addEventListener('pointerup', onPointerUp);
  renderer.domElement.addEventListener('pointercancel', () => { pointer = null; });
  renderer.domElement.addEventListener('webglcontextlost', event => {
    event.preventDefault();
    viewport.classList.remove('is-3d-ready');
    cancelAnimationFrame(frame);
    frame = 0;
  });

  setGroup(initialItems, 1, true);
  viewport.classList.add('is-3d-ready');
  frame = requestAnimationFrame(loop);

  return {
    setProgress(value) {
      stage = clamp(value, 0, 3);
      if (reduced) draw(performance.now());
    },
    setGroup(items, direction = 1, immediate = false) {
      setGroup(items, direction, immediate);
      if (reduced) draw(performance.now());
    },
    focus(slot, enabled = true) {
      focus(slot, enabled);
      if (reduced) draw(performance.now());
    },
    nudge(slot) {
      nudge(slot);
      if (reduced) draw(performance.now());
    },
    setPinchZoom(value) {
      setPinchZoom(value);
      if (reduced) draw(performance.now());
    },
    setZoomAnchor,
    dispose() {
      stopped = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      resizeObserver.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      renderer.domElement.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('pointerup', onPointerUp);
      holders.forEach(holder => holder.material.map?.dispose());
      scene.traverse(object => {
        if (!object.isMesh) return;
        object.geometry?.dispose();
        if (Array.isArray(object.material)) object.material.forEach(material => material.dispose());
        else object.material?.dispose();
      });
      renderer.dispose();
      renderer.domElement.remove();
    }
  };
}

function initAll(scope = document) {
  scope.querySelectorAll('[data-wardrobe]').forEach(initWardrobe);
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), {once:true});
else initAll();
document.addEventListener('shopify:section:load', event => initAll(event.target));
document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('[data-wardrobe]').forEach(element => instances.get(element)?.dispose()));

export { createRoom };
